import GridItemList, { infoLabel, Row } from 'components/GridItemList';
import InfoTooltip from 'components/InfoTooltip.tsx';
import { FormattedDate } from 'components/misc/FormattedDate';
import { RecordLink } from 'components/record-chip/RecordLink';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { ClassifierData } from 'types/classifiers';
import { MatterDto } from 'types/matters';
import { getClassifierText, t } from 'utils/getTranslations';
import { MatterSection } from '../form-sections/MatterSection';
import { ENTITY_LINK_CLASS } from '../util';

interface Props {
    matter: MatterDto;
    classifiers: Record<string, ClassifierData>;
}

const OtherView: React.FC<Props> = ({ matter, classifiers }) => {
    const isNew = useIsNewDesign();
    const otherRows: Row[] = [
        {
            content: <FormattedMessage id={matter.isExcludedFromXR ? 'yes' : 'no'} />,
            // The new design's label is its field's, as on every card that edits in place.
            label: isNew ? (
                infoLabel('matters.excluded-from-xr', 'matters.other.excluded-from-xr-tooltip')
            ) : (
                <div className="flex items-center gap-1">
                    <FormattedMessage id="matters.other.excluded-from-xr" />
                    <InfoTooltip text="matters.other.excluded-from-xr-tooltip" />
                </div>
            ),
        },
    ];
    const paperFile: Row = {
        content: <FormattedMessage id={matter.isPaperFile ? 'yes' : 'no'} />,
        label: isNew ? (
            infoLabel('matters.paper-file-exists', 'matters.tooltips.paper-file-exists')
        ) : (
            <FormattedMessage id="matters.paper-file-exists" />
        ),
    };
    const closingExempt: Row = {
        content: matter.isAutomaticClosingExempt ? t('yes') : t('no'),
        label: t('matters.exempt-from-automatic-closing'),
    };
    const archivingRows: Row[] = [
        // The new design lists the rows in its form's order, so a row and its field share a cell.
        ...(isNew ? [closingExempt, paperFile] : [paperFile, closingExempt]),
        {
            content: <FormattedDate value={matter.archivedDate} />,
            label: t('matters.archiving-date'),
        },
        {
            content: matter.archivedInOffice ? (
                <RecordLink
                    className={ENTITY_LINK_CLASS}
                    to={`/office/${matter.archivedInOffice.id}`}
                    label={matter.archivedInOffice.name}
                />
            ) : (
                '-'
            ),
            label: t('matters.archived-in-office'),
        },
        {
            content: matter.archivingPropertiesTypeCode
                ? getClassifierText(classifiers['ARCHIVING_PROPERTIES_TYPE'], matter.archivingPropertiesTypeCode)
                : '-',
            label: t('matters.archiving-properties'),
        },
        {
            content: matter.archivingNotes || '-',
            label: t('matters.archiving-notes'),
            multiline: true,
        },
    ];

    if (!isNew) {
        return <GridItemList size={12} data={[...otherRows, ...archivingRows]} key="matter-archived" />;
    }
    // The new design shows the form's two sections, each with its own Edit.
    return (
        <div className="flex flex-col gap-(--card-gap)">
            <MatterSection id="archiving" titleId="matters.archiving" matter={matter}>
                {(edit) => <GridItemList isNew titleId="matters.archiving" data={archivingRows} button={edit} />}
            </MatterSection>
            <MatterSection id="other" titleId="matters.other.title" matter={matter}>
                {(edit) => <GridItemList isNew titleId="matters.other.title" data={otherRows} button={edit} />}
            </MatterSection>
        </div>
    );
};

export default OtherView;
